import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Surface,
  eyebrowStyle,
  ghostButtonStyle,
  ghostLinkStyle,
  inputStyle,
  pageStyle,
  primaryButtonStyle,
} from '@kilat/ui';
import { Link } from 'react-router-dom';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { logger } from '../lib/logger.js';
import { PAGE_FRAME_MAX, dashboardPageStyle } from '../lib/layout.js';
import {
  callListRecentPayouts,
  callMarkPayoutPaid,
  callRunPayoutBatch,
  type RecentPayoutRow,
  type RunPayoutBatchOutput,
} from '../lib/callables/payouts.js';
import { money, renderDuitNowCsv, type DuitNowCsvRow } from '@kilat/shared';

/**
 * /superadmin/payouts — Sprint 97.
 *
 * Closes the commission loop: agents and recruiters earn via the
 * `commissionAccrue` trigger; this page is where the platform owner
 * actually pays them.
 *
 * Two cards, switchable via a "Pay agents | Pay recruiters" tab. Each
 * card shows:
 *   - "Run batch" button → calls `runPayoutBatch` → returns a summary
 *     (scanned / paidOut / totalGrossSen / per-recipient skipped reason)
 *   - Recent payouts table (last 50) with status pills, per-row Mark
 *     paid button (writes the debit ledger entry + flips status), and
 *     a "Download DuitNow CSV" toolbar above the pending-payouts subset
 *
 * Every state explicit per [no-dead-ends]: loading / empty / error /
 * batch-running / batch-complete-summary / mark-paid-success.
 *
 * Brand-scoped: the super-admin claim is `{ superadmin: { [brandId]: true } }`
 * (ADR-0017), so brand-A super-admins never see brand-B recipients.
 * The runPayoutBatch + listRecentPayouts callables filter by brandId
 * server-side; this UI passes the active brand id from
 * `usePlatformBrand()`.
 */

type RecipientKind = 'agent' | 'recruiter' | 'tenant';

export function SuperadminPayoutsPage() {
  const { claims, loading: authLoading } = useAuth();
  const brand = usePlatformBrand();
  const { t } = useI18n();
  const isSuper = useMemo(() => isSuperAdminForBrand(claims, brand.id), [claims, brand.id]);
  const [kind, setKind] = useState<RecipientKind>('agent');

  if (authLoading) return <Screen title={t('superadmin.payouts.loadingTitle')} />;
  if (!isSuper) {
    return (
      <Screen
        title={t('superadmin.payouts.guardTitle')}
        body={t('superadmin.payouts.guardBody', { brand: brand.name })}
        cta={{ label: t('superadmin.payouts.guardCta'), to: '/queue' }}
      />
    );
  }

  return (
    <main style={dashboardPageStyle}>
      <header style={headerStyle}>
        <div style={{ maxWidth: PAGE_FRAME_MAX, margin: '0 auto', padding: 'var(--space-5)' }}>
          <p style={eyebrowStyle}>{t('superadmin.payouts.eyebrow')}</p>
          <h1
            style={{ margin: 'var(--space-1) 0 0 0', fontSize: 'var(--text-h1)', fontWeight: 600 }}
          >
            {t('superadmin.payouts.title')}
          </h1>
          <p
            style={{
              margin: 'var(--space-2) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              maxWidth: 720,
            }}
          >
            {t('superadmin.payouts.subtitle')}
          </p>
          <div
            style={{
              marginTop: 'var(--space-4)',
              display: 'flex',
              gap: 'var(--space-3)',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Link to="/queue" style={ghostLinkStyle}>
              {t('superadmin.payouts.backToQueue')}
            </Link>
            <KindTabs value={kind} onChange={setKind} />
          </div>
        </div>
      </header>

      <section style={contentStyle}>
        <PayoutsPanel kind={kind} brandId={brand.id} brandName={brand.name} />
      </section>
    </main>
  );
}

function KindTabs({
  value,
  onChange,
}: {
  readonly value: RecipientKind;
  readonly onChange: (v: RecipientKind) => void;
}) {
  const { t } = useI18n();
  return (
    <div
      role="tablist"
      aria-label={t('superadmin.payouts.tabsLabel')}
      style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}
    >
      {(['tenant', 'agent', 'recruiter'] as const).map((k) => {
        const sel = k === value;
        return (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={sel}
            onClick={() => onChange(k)}
            style={{
              padding: '8px 16px',
              borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--color-border-strong)',
              background: sel ? 'var(--color-primary)' : 'var(--color-surface)',
              color: sel ? 'var(--color-primary-fg)' : 'var(--color-on-surface)',
              fontSize: 'var(--text-meta)',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {t(`superadmin.payouts.tabs.${k}`)}
          </button>
        );
      })}
    </div>
  );
}

// ── Main panel ─────────────────────────────────────────────────────────

function PayoutsPanel({
  kind,
  brandId,
  brandName,
}: {
  readonly kind: RecipientKind;
  readonly brandId: string;
  readonly brandName: string;
}) {
  const { t } = useI18n();
  const [payouts, setPayouts] = useState<RecentPayoutRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [batchState, setBatchState] = useState<
    | { kind: 'idle' }
    | { kind: 'running' }
    | { kind: 'done'; result: RunPayoutBatchOutput }
    | { kind: 'failed'; message: string }
  >({ kind: 'idle' });
  const [markingId, setMarkingId] = useState<string | null>(null);
  const [markError, setMarkError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoadError(null);
    setPayouts(null);
    try {
      const out = await callListRecentPayouts({ kind, brandId, limit: 50 });
      setPayouts(out.payouts);
    } catch (e) {
      logger.error('listRecentPayouts failed', { error: String(e) });
      setLoadError(extractFriendlyError(e));
    }
  }, [kind, brandId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function runBatch() {
    setBatchState({ kind: 'running' });
    try {
      const result = await callRunPayoutBatch({ kind, brandId });
      setBatchState({ kind: 'done', result });
      // Refresh the list — newly-created payouts show up.
      await reload();
    } catch (e) {
      logger.error('runPayoutBatch failed', { error: String(e) });
      setBatchState({ kind: 'failed', message: extractFriendlyError(e) });
    }
  }

  async function markPaid(row: RecentPayoutRow, txRef: string) {
    setMarkingId(row.payoutId);
    setMarkError(null);
    try {
      await callMarkPayoutPaid({
        kind,
        brandId,
        recipientId: row.recipientId,
        payoutId: row.payoutId,
        ...(txRef.trim() ? { txRef: txRef.trim() } : {}),
      });
      await reload();
    } catch (e) {
      logger.error('markPayoutPaid failed', { error: String(e) });
      setMarkError(extractFriendlyError(e));
    } finally {
      setMarkingId(null);
    }
  }

  const pending = useMemo(
    () => (payouts ?? []).filter((p) => p.status === 'pending' || p.status === 'processing'),
    [payouts],
  );

  function downloadPendingCsv() {
    if (pending.length === 0) return;
    const rows: DuitNowCsvRow[] = pending.map((p) => {
      const duitNowId = p.destination.duitNowId;
      const base = {
        recipientName: p.recipientName ?? p.recipientId,
        amountSen: p.grossSen,
        reference: p.payoutId,
        description: `${recipientNounCap(kind)} ${kind === 'tenant' ? 'wash payout' : 'commission'}${p.recipientCode ? ' · ' + p.recipientCode : ''}`,
      };
      if (duitNowId) {
        return { ...base, idType: 'DUITNOW_ID' as const, id: duitNowId };
      }
      return {
        ...base,
        idType: 'ACCOUNT' as const,
        id: p.destination.accountNumber ?? '(missing account)',
        ...(p.destination.bankName ? { bankName: p.destination.bankName } : {}),
      };
    });
    const { csv, filename } = renderDuitNowCsv({
      rows,
      generatedAtMs: Date.now(),
      brandName,
    });
    triggerCsvDownload(csv, filename);
  }

  return (
    <div style={twoColStyle}>
      {/* Batch runner card */}
      <Surface as="section">
        <h2 style={sectionTitleStyle}>{t(`superadmin.payouts.batch.title.${kind}`)}</h2>
        <p style={sectionHintStyle}>
          {t(`superadmin.payouts.batch.hint.${kind}`, { brand: brandName })}
        </p>

        {batchState.kind === 'idle' || batchState.kind === 'running' ? (
          <button
            type="button"
            onClick={() => void runBatch()}
            disabled={batchState.kind === 'running'}
            style={{
              ...primaryButtonStyle,
              marginTop: 'var(--space-4)',
              opacity: batchState.kind === 'running' ? 0.6 : 1,
              cursor: batchState.kind === 'running' ? 'wait' : 'pointer',
            }}
          >
            {batchState.kind === 'running'
              ? t('superadmin.payouts.batch.running')
              : t('superadmin.payouts.batch.run')}
          </button>
        ) : null}

        {batchState.kind === 'failed' ? (
          <div style={{ marginTop: 'var(--space-3)' }}>
            <InlineNote tone="error">{batchState.message}</InlineNote>
            <button
              type="button"
              onClick={() => setBatchState({ kind: 'idle' })}
              style={{ ...ghostButtonStyle, marginTop: 'var(--space-3)' }}
            >
              {t('superadmin.payouts.batch.tryAgain')}
            </button>
          </div>
        ) : null}

        {batchState.kind === 'done' ? (
          <BatchSummary
            result={batchState.result}
            onClose={() => setBatchState({ kind: 'idle' })}
          />
        ) : null}
      </Surface>

      {/* Recent payouts list */}
      <Surface as="section">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
          }}
        >
          <h2 style={sectionTitleStyle}>{t('superadmin.payouts.recent.title')}</h2>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={downloadPendingCsv}
              disabled={pending.length === 0}
              style={{
                ...ghostButtonStyle,
                opacity: pending.length === 0 ? 0.5 : 1,
                cursor: pending.length === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              {t('superadmin.payouts.recent.csv', { count: pending.length })}
            </button>
            <button type="button" onClick={() => void reload()} style={ghostButtonStyle}>
              {t('superadmin.payouts.recent.refresh')}
            </button>
          </div>
        </div>
        <p style={sectionHintStyle}>{t(`superadmin.payouts.recent.hint.${kind}`)}</p>

        {loadError ? (
          <div style={{ marginTop: 'var(--space-3)' }}>
            <InlineNote tone="error">{loadError}</InlineNote>
            <button
              type="button"
              onClick={() => void reload()}
              style={{ ...ghostButtonStyle, marginTop: 'var(--space-3)' }}
            >
              {t('superadmin.payouts.recent.retry')}
            </button>
          </div>
        ) : payouts === null ? (
          <div style={{ marginTop: 'var(--space-4)' }}>
            <SkeletonRow />
          </div>
        ) : payouts.length === 0 ? (
          <InlineNote>{t(`superadmin.payouts.recent.empty.${kind}`)}</InlineNote>
        ) : (
          <div style={{ marginTop: 'var(--space-4)', display: 'grid', gap: 'var(--space-2)' }}>
            {markError ? <InlineNote tone="error">{markError}</InlineNote> : null}
            {payouts.map((p) => (
              <PayoutRow
                key={p.payoutId}
                payout={p}
                kindLabel={t(`superadmin.payouts.noun.${kind}`)}
                onMarkPaid={(tx) => void markPaid(p, tx)}
                marking={markingId === p.payoutId}
              />
            ))}
          </div>
        )}
      </Surface>
    </div>
  );
}

// ── Batch summary card ────────────────────────────────────────────────

function BatchSummary({
  result,
  onClose,
}: {
  readonly result: RunPayoutBatchOutput;
  readonly onClose: () => void;
}) {
  const { t } = useI18n();
  return (
    <div
      style={{
        marginTop: 'var(--space-4)',
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-md)',
        background: 'rgba(13,122,79,0.06)',
        border: '1px solid rgba(13,122,79,0.22)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 'var(--space-3)',
        }}
      >
        <p style={{ margin: 0, fontWeight: 600, color: 'var(--color-primary)' }}>
          {t('superadmin.payouts.summary.headline', {
            paid: result.paidOut,
            scanned: result.scanned,
            total: money.format(result.totalGrossSen),
          })}
        </p>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--color-on-surface-muted)',
            cursor: 'pointer',
            fontSize: 18,
          }}
          aria-label={t('superadmin.payouts.summary.dismiss')}
        >
          ×
        </button>
      </header>
      {result.payouts.length > 0 ? (
        <div>
          <p style={{ margin: 0, fontSize: 'var(--text-meta)', fontWeight: 600 }}>
            {t('superadmin.payouts.summary.created')}
          </p>
          <ul
            style={{
              margin: 'var(--space-2) 0 0 0',
              padding: 0,
              listStyle: 'none',
              display: 'grid',
              gap: 4,
            }}
          >
            {result.payouts.map((p) => (
              <li
                key={p.payoutId}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 'var(--text-meta)',
                }}
              >
                <span>{p.recipientId}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {p.itemCount === 1
                    ? t('superadmin.payouts.summary.entriesOne', {
                        amount: money.format(p.grossSen),
                      })
                    : t('superadmin.payouts.summary.entriesOther', {
                        amount: money.format(p.grossSen),
                        count: p.itemCount,
                      })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {result.skipped.length > 0 ? (
        <details>
          <summary
            style={{
              cursor: 'pointer',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {result.skipped.length === 1
              ? t('superadmin.payouts.summary.skippedOne')
              : t('superadmin.payouts.summary.skippedOther', { count: result.skipped.length })}
          </summary>
          <ul
            style={{
              margin: 'var(--space-2) 0 0 0',
              padding: 0,
              listStyle: 'none',
              display: 'grid',
              gap: 4,
            }}
          >
            {result.skipped.map((s) => (
              <li
                key={s.recipientId}
                style={{
                  fontSize: 'var(--text-meta)',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <span>{s.recipientId}</span>
                <span style={{ color: 'var(--color-on-surface-muted)' }}>
                  {t(skipReasonKey(s.reason))}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      <p
        style={{ margin: 0, fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}
      >
        {t('superadmin.payouts.summary.nextStep')}
      </p>
    </div>
  );
}

function skipReasonKey(
  reason:
    | 'below_threshold'
    | 'no_destination'
    | 'inactive'
    | 'no_eligible_entries'
    | 'kyc_unverified',
): string {
  switch (reason) {
    case 'below_threshold':
      return 'superadmin.payouts.skip.belowThreshold';
    case 'no_destination':
      return 'superadmin.payouts.skip.noDestination';
    case 'inactive':
      return 'superadmin.payouts.skip.inactive';
    case 'no_eligible_entries':
      return 'superadmin.payouts.skip.noEligibleEntries';
    case 'kyc_unverified':
      return 'superadmin.payouts.skip.kycUnverified';
  }
}

// ── Payout row ─────────────────────────────────────────────────────────

function PayoutRow({
  payout,
  kindLabel,
  onMarkPaid,
  marking,
}: {
  readonly payout: RecentPayoutRow;
  readonly kindLabel: string;
  readonly onMarkPaid: (txRef: string) => void;
  readonly marking: boolean;
}) {
  const { t } = useI18n();
  const [showMark, setShowMark] = useState(false);
  const [txRef, setTxRef] = useState('');
  const dest = payout.destination;
  const destLabel = dest.duitNowId
    ? `DuitNow ${dest.duitNowId}`
    : dest.accountNumber
      ? `${dest.bankName ?? t('superadmin.payouts.row.bankFallback')} ${dest.accountNumber}`
      : t('superadmin.payouts.row.noDestination');
  const recipientLabel = payout.recipientName ?? payout.recipientId;
  return (
    <article
      style={{
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
        background: 'var(--color-surface)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 500 }}>
            {recipientLabel}
            {payout.recipientCode ? (
              <span
                style={{
                  fontFamily: 'ui-monospace, monospace',
                  color: 'var(--color-on-surface-muted)',
                  fontWeight: 400,
                  marginLeft: 8,
                }}
              >
                ({payout.recipientCode})
              </span>
            ) : null}
          </p>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {destLabel} ·{' '}
            {payout.itemCount === 1
              ? t('superadmin.payouts.row.ledgerOne')
              : t('superadmin.payouts.row.ledgerOther', { count: payout.itemCount })}{' '}
            · {t('superadmin.payouts.row.initiated', { date: formatDate(payout.initiatedAtMs) })}
            {payout.paidAtMs
              ? ` · ${t('superadmin.payouts.row.paid', { date: formatDate(payout.paidAtMs) })}`
              : ''}
            {payout.txRef ? ` · ${t('superadmin.payouts.row.ref', { ref: payout.txRef })}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <StatusPill status={payout.status} />
          <span
            style={{
              fontSize: 'var(--text-h2)',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {money.format(payout.grossSen)}
          </span>
        </div>
      </div>

      {(payout.status === 'pending' || payout.status === 'processing') && !showMark ? (
        <div>
          <button type="button" onClick={() => setShowMark(true)} style={ghostButtonStyle}>
            {t('superadmin.payouts.row.markPaid')}
          </button>
        </div>
      ) : null}

      {(payout.status === 'pending' || payout.status === 'processing') && showMark ? (
        <div
          style={{
            marginTop: 'var(--space-2)',
            padding: 'var(--space-3)',
            borderRadius: 'var(--radius-md)',
            background: 'rgba(13,122,79,0.04)',
            border: '1px dashed rgba(13,122,79,0.22)',
            display: 'grid',
            gap: 'var(--space-2)',
          }}
        >
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
              {t('superadmin.payouts.row.txRefLabel', { noun: kindLabel })}
            </span>
            <input
              type="text"
              value={txRef}
              onChange={(e) => setTxRef(e.target.value)}
              placeholder={t('superadmin.payouts.row.txRefPlaceholder')}
              maxLength={120}
              style={inputStyle}
              autoFocus
            />
          </label>
          <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => {
                setShowMark(false);
                setTxRef('');
              }}
              disabled={marking}
              style={ghostButtonStyle}
            >
              {t('superadmin.payouts.row.cancel')}
            </button>
            <button
              type="button"
              onClick={() => onMarkPaid(txRef)}
              disabled={marking}
              style={{
                ...primaryButtonStyle,
                opacity: marking ? 0.6 : 1,
                cursor: marking ? 'wait' : 'pointer',
              }}
            >
              {marking
                ? t('superadmin.payouts.row.recording')
                : t('superadmin.payouts.row.confirmPaid', {
                    amount: money.format(payout.grossSen),
                  })}
            </button>
          </div>
        </div>
      ) : null}
    </article>
  );
}

function StatusPill({ status }: { readonly status: RecentPayoutRow['status'] }) {
  const { t } = useI18n();
  const { bg, fg, label } = (() => {
    switch (status) {
      case 'pending':
        return {
          bg: 'rgba(245,158,11,0.12)',
          fg: '#b45309',
          label: t('superadmin.payouts.status.pending'),
        };
      case 'processing':
        return {
          bg: 'rgba(59,130,246,0.12)',
          fg: '#1d4ed8',
          label: t('superadmin.payouts.status.processing'),
        };
      case 'paid':
        return {
          bg: 'rgba(13,122,79,0.10)',
          fg: 'var(--color-primary)',
          label: t('superadmin.payouts.status.paid'),
        };
      case 'failed':
        return {
          bg: 'rgba(239,68,68,0.12)',
          fg: '#991b1b',
          label: t('superadmin.payouts.status.failed'),
        };
    }
  })();
  return (
    <span
      style={{
        padding: '2px 10px',
        borderRadius: 'var(--radius-pill)',
        background: bg,
        color: fg,
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

function triggerCsvDownload(csv: string, filename: string): void {
  // Prefix with UTF-8 BOM so Excel doesn't mangle special characters.
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the browser a tick to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

function formatDate(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function extractFriendlyError(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const m = String((err as { message: string }).message);
    if (m.includes('permission-denied')) {
      return "You don't have permission to run payouts for this brand.";
    }
    if (m.includes('failed-precondition')) {
      return m.replace(/^.*?failed-precondition[:\s]*/i, '');
    }
    return m;
  }
  return 'Something went wrong. Try again.';
}

function recipientNounCap(kind: RecipientKind): string {
  return kind === 'tenant' ? 'Operator' : kind === 'agent' ? 'Agent' : 'Recruiter';
}

function isSuperAdminForBrand(claims: Record<string, unknown> | null, brandId: string): boolean {
  if (!claims) return false;
  const sa = (claims as { superadmin?: Record<string, unknown> }).superadmin;
  if (!sa || typeof sa !== 'object') return false;
  return sa[brandId] === true;
}

// ── Reusable bits + styles (mirrors superadmin-plans patterns) ─────────

function InlineNote({
  children,
  tone = 'muted',
}: {
  readonly children: React.ReactNode;
  readonly tone?: 'muted' | 'error';
}) {
  return (
    <p
      role={tone === 'error' ? 'alert' : undefined}
      style={{
        margin: 0,
        padding: '12px 14px',
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--text-meta)',
        background: tone === 'error' ? 'rgba(239,68,68,0.06)' : 'var(--tint-subtle)',
        border: `1px solid ${tone === 'error' ? 'rgba(239,68,68,0.20)' : 'var(--color-border)'}`,
        color: tone === 'error' ? '#991b1b' : 'var(--color-on-surface-muted)',
      }}
    >
      {children}
    </p>
  );
}

function SkeletonRow() {
  return (
    <div
      style={{
        height: 100,
        borderRadius: 12,
        background: 'var(--tint-subtle)',
        animation: 'kilatPulse 1.2s ease-in-out infinite',
      }}
    >
      <style>{`@keyframes kilatPulse { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }`}</style>
    </div>
  );
}

function Screen({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body?: string;
  readonly cta?: { label: string; to: string };
}) {
  return (
    <main style={{ ...pageStyle, display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center', maxWidth: 480, padding: 'var(--space-6)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h1>
        {body ? (
          <p
            style={{
              margin: 'var(--space-3) 0 0 0',
              color: 'var(--color-on-surface-muted)',
              lineHeight: 1.5,
            }}
          >
            {body}
          </p>
        ) : null}
        {cta ? (
          <Link
            to={cta.to}
            style={{
              display: 'inline-block',
              marginTop: 'var(--space-4)',
              padding: '12px 22px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-primary)',
              color: 'var(--color-primary-fg)',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            {cta.label}
          </Link>
        ) : null}
      </div>
    </main>
  );
}

const headerStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  borderBottom: '1px solid var(--color-border)',
};

const contentStyle: React.CSSProperties = {
  maxWidth: PAGE_FRAME_MAX,
  margin: '0 auto',
  padding: 'var(--space-5)',
};

const twoColStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))',
  gap: 'var(--space-5)',
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-h2)',
  fontWeight: 600,
};

const sectionHintStyle: React.CSSProperties = {
  margin: 'var(--space-2) 0 0 0',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  lineHeight: 1.5,
};
